/**
 * Admin User Detail
 */
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { MIN_PASSWORD_LENGTH } from "@jourfx/convex/lib/password";
import {
  ArrowLeft,
  Save,
  Loader2,
  Shield,
  User,
  AlertTriangle,
  KeyRound,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Badge,
  useToast,
  Skeleton,
} from "@jourfx/ui";
import { PageHeader } from "../../components/PageHeader";
import { useSession } from "../../lib/auth";

type Id<T extends string> = string & { __tableName: T };

function getAdminRoleLabel(
  t: (key: string) => string,
  role: string
): string {
  if (role === "super_admin") {
    return t("roles.super_admin");
  }
  return t("roles.administrator");
}

type OrganizationOption = { _id: string; name: string };

export function AdminDetail() {
  const { adminId } = useParams({ strict: false }) as { adminId: string };
  const { t } = useTranslation("admins");
  const { t: tc } = useTranslation("common");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isSuperAdmin } = useSession();

  const admin = useQuery(api.admin.admins.get, { id: adminId as Id<"adminUsers"> });
  const organizations = useQuery(
    api.admin.organizations.list,
    { status: "active", limit: 100 }
  ) as { organizations: OrganizationOption[] } | undefined;
  const updateAdmin = useMutation(api.admin.admins.update);
  const promoteAdmin = useMutation(api.admin.admins.promote);
  const demoteAdmin = useMutation(api.admin.admins.demote);
  const setPassword = useMutation(api.admin.admins.setPassword);

  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");
  const [organizationId, setOrganizationId] = useState("");
  const [demotionOrganizationId, setDemotionOrganizationId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [isDemoting, setIsDemoting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  useEffect(() => {
    if (admin) {
      setName(admin.name || "");
      setStatus(admin.status || "active");
      setOrganizationId(admin.organizationId || "");
      setDemotionOrganizationId("");
    }
  }, [admin]);

  if (admin === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (admin === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertTriangle className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">
          {t("detail.notFound")}
        </h2>
        <Link to="/admins" className="text-primary hover:underline">
          {t("detail.backToList")}
        </Link>
      </div>
    );
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAdmin({
        id: admin._id as Id<"adminUsers">,
        name,
        organizationId:
          isSuperAdmin && admin.role === "administrator"
            ? (organizationId as any)
            : undefined,
        status: status as any,
      });
      toast({ title: t("toasts.updated.title") });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePromote = async () => {
    setIsPromoting(true);
    try {
      await promoteAdmin({ adminId: admin._id as Id<"adminUsers"> });
      toast({ title: t("toasts.promoted.title") });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsPromoting(false);
    }
  };

  const handleDemote = async () => {
    setIsDemoting(true);
    try {
      await demoteAdmin({
        adminId: admin._id as Id<"adminUsers">,
        organizationId: demotionOrganizationId as any,
      });
      toast({ title: t("toasts.demoted.title") });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsDemoting(false);
    }
  };

  const handleSetPassword = async () => {
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError(t("detail.passwordMismatch"));
      return;
    }

    setIsSettingPassword(true);
    try {
      await setPassword({
        id: admin._id as Id<"adminUsers">,
        newPassword,
      });
      setNewPassword("");
      setConfirmPassword("");
      toast({ title: t("toasts.passwordUpdated.title") });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSettingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("detail.title")}
        description={t("detail.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate({ to: "/admins" })}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("detail.back")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                isSaving ||
                (isSuperAdmin &&
                  admin.role === "administrator" &&
                  !organizationId)
              }
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {t("detail.save")}
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t("detail.profile")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>{t("detail.name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t("detail.email")}</Label>
            <Input value={admin.email} disabled />
          </div>
          <div className="space-y-2">
            <Label>{t("detail.status")}</Label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <option value="active">{t("detail.statusOptions.active")}</option>
              <option value="inactive">{t("detail.statusOptions.inactive")}</option>
              <option value="locked">{t("detail.statusOptions.locked")}</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>{t("detail.role")}</Label>
            <div className="flex items-center gap-2">
              <Badge variant={admin.role === "super_admin" ? "default" : "secondary"}>
                {getAdminRoleLabel(t, admin.role)}
              </Badge>
              {admin.organizationName && (
                <span className="text-sm text-muted-foreground">
                  ({admin.organizationName})
                </span>
              )}
            </div>
          </div>
          {isSuperAdmin && admin.role === "administrator" ? (
            <div className="space-y-2">
              <Label htmlFor="admin-organization">{t("detail.organization")}</Label>
              <select
                id="admin-organization"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">{t("detail.organizationPlaceholder")}</option>
                {organizations?.organizations?.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            {t("detail.password")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("detail.passwordDescription")}
          </p>
          <div className="space-y-2">
            <Label htmlFor="admin-new-password">{t("detail.newPassword")}</Label>
            <Input
              id="admin-new-password"
              type="password"
              value={newPassword}
              minLength={MIN_PASSWORD_LENGTH}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-confirm-password">{t("detail.confirmPassword")}</Label>
            <Input
              id="admin-confirm-password"
              type="password"
              value={confirmPassword}
              minLength={MIN_PASSWORD_LENGTH}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {passwordError ? (
            <p className="text-sm text-destructive">{passwordError}</p>
          ) : null}
          <div className="flex justify-end">
            <Button
              onClick={handleSetPassword}
              disabled={
                isSettingPassword ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword
              }
            >
              {isSettingPassword ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="mr-2 h-4 w-4" />
              )}
              {t("detail.setPassword")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t("detail.roleActions")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {admin.role === "administrator" ? (
              <Button variant="outline" onClick={handlePromote} disabled={isPromoting}>
                {isPromoting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Shield className="mr-2 h-4 w-4" />
                )}
                {t("detail.promoteToSuperAdmin")}
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="demotion-organization">
                    {t("detail.demoteOrganization")}
                  </Label>
                  <select
                    id="demotion-organization"
                    value={demotionOrganizationId}
                    onChange={(e) => setDemotionOrganizationId(e.target.value)}
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="">{t("detail.organizationPlaceholder")}</option>
                    {organizations?.organizations?.map((organization) => (
                      <option key={organization._id} value={organization._id}>
                        {organization.name}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline"
                  onClick={handleDemote}
                  disabled={isDemoting || !demotionOrganizationId}
                >
                  {isDemoting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Shield className="mr-2 h-4 w-4" />
                  )}
                  {t("detail.demoteToAdmin")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
